import type { Subscription } from '@xhubio-saas/core-frontend/plugin-subscription'

/**
 * The states the billing header knows (plan 2026-10-11/02, E9).
 *
 * ⚪ `paused` is not in the plan's table, but the billing backend can report it — it gets its own word instead of
 * pretending to be `active`.
 */
export type BillingState = 'active' | 'trial' | 'canceled' | 'pastDue' | 'paused' | 'free'

/** The tabs of the page, in display order. They travel as `?tab=` in the URL. */
export const BILLING_TABS = ['overview', 'plans', 'usage', 'invoices'] as const
export type BillingTab = (typeof BILLING_TABS)[number]

/** Key under which the page remembers the last tab for the session (like the validation workbench). */
export const BILLING_TAB_STORAGE_KEY = 'billing:tab'

export function isBillingTab(value: string | null | undefined): value is BillingTab {
  return (
    value !== null && value !== undefined && (BILLING_TABS as readonly string[]).includes(value)
  )
}

/**
 * Free = no subscription, or the free plan. 🔴 The default free subscription that billing creates once a plan is
 * active HAS an id — gating on `subscription.id` alone would offer a portal for nothing (same rule as core-frontend
 * `SubscriptionPage.isPaidPlan`).
 */
export function isFreeSubscription(subscription: Subscription | null): boolean {
  return !subscription || subscription.planId === 'free'
}

/**
 * The one state the header carries.
 *
 * Order matters: a failed payment is the most urgent thing to say; a scheduled cancellation (`cancelAt` in the
 * future) keeps `status: 'active'` at the provider but reads as „canceled, ends on …" — that is what the customer
 * needs to know.
 */
export function billingStateOf(
  subscription: Subscription | null,
  now: Date = new Date()
): BillingState {
  if (isFreeSubscription(subscription) || !subscription) return 'free'
  const { status } = subscription
  if (status === 'past_due' || status === 'incomplete' || status === 'incomplete_expired') {
    return 'pastDue'
  }
  if (status === 'canceled') return 'canceled'
  if (subscription.cancelAt && subscription.cancelAt.getTime() > now.getTime()) return 'canceled'
  if (status === 'trialing') return 'trial'
  if (status === 'paused') return 'paused'
  return 'active'
}

/** The date the state sentence names — renewal, trial end, end of the canceled plan. None for the others. */
export function stateDateOf(
  state: BillingState,
  subscription: Subscription | null
): Date | undefined {
  if (!subscription) return undefined
  switch (state) {
    case 'active':
      return subscription.currentPeriodEnd
    case 'trial':
      return subscription.trialEnd ?? subscription.currentPeriodEnd
    case 'canceled':
      return subscription.cancelAt ?? subscription.currentPeriodEnd
    default:
      return undefined
  }
}

/** Day in the UI language — `11. Okt. 2026` / `Oct 11, 2026`. */
export function formatDay(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date)
}

/** i18n language code → locale for number and date formatting (same map the former wrappers used). */
const LOCALE_BY_LANGUAGE: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-PT',
  cs: 'cs-CZ',
  hu: 'hu-HU',
  ro: 'ro-RO',
  bg: 'bg-BG',
}

export function localeOf(language: string | undefined): string {
  return LOCALE_BY_LANGUAGE[(language ?? 'en').split('-')[0] ?? 'en'] ?? 'en-US'
}
